import { vaultService } from '../vaultService';
import { createLogger } from '../../utils/logger';
import { getCachedWrappingSecretKey } from './MasterKeyService';
import {
  openSealedMasterKey,
  TrustedPartyWrappingError,
} from './TrustedPartyWrapping';

/**
 * High-level helper for the consumer side of the trusted-party flow.
 *
 * Successors (post-death) and keyholders (live) call these when they need
 * to decrypt a primary's data. The flow is:
 *
 *   1. Fetch the sealed master key the primary stored for this user
 *      (backend enforces the role-specific gate — 403 means "not yet").
 *   2. Read the user's own cached wrapping secret key.
 *   3. Open the sealed blob locally — primary's master key in hand.
 *
 * Results are cached in-memory for the session so repeated reads across
 * screens don't repeatedly unseal. The cache is process-local — killed
 * on any background / relaunch (nothing persists to disk here).
 *
 * The CACHED primary master key is what the surrounding document /
 * credentials decryption paths then consume; this module does not itself
 * read or write any encrypted data.
 */

const logger = createLogger('TrustedPartyAccess');

// In-memory cache: primaryUserId → { masterKey, role, openedAt }. Not
// written to disk — the MasterKeyService already caches this user's OWN
// master key in the keychain; caching someone ELSE's master key there
// would bloat the biometrically-gated keychain and complicate logout.
const sessionCache = new Map<
  string,
  {
    masterKey: string;
    role: 'successor' | 'keyholder';
    openedAt: number;
  }
>();

export class TrustedPartyAccessNotReadyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrustedPartyAccessNotReadyError';
  }
}

export class TrustedPartyAccessDeniedError extends Error {
  constructor() {
    super(
      'Access to this user\'s vault is not available. The backend has not gated you in — check that the death event has been verified (successor) or an access grant exists (keyholder).',
    );
    this.name = 'TrustedPartyAccessDeniedError';
  }
}

/**
 * Fetch + unseal a primary's master key for this trusted party. Returns
 * the unwrapped master key (hex). Throws:
 *   - TrustedPartyAccessNotReadyError if this user's wrapping secret key
 *     isn't cached (they need to unlock their own vault first).
 *   - TrustedPartyAccessDeniedError if the backend returned null (403/404
 *     collapsed in the vault-service layer).
 *   - TrustedPartyWrappingError if the sealed blob fails to open (should
 *     not happen in practice — would indicate tampering or a rotated
 *     keypair the primary never re-wrapped for).
 */
export async function fetchAndOpenPrimaryMasterKey(
  primaryUserId: string,
): Promise<string> {
  const cached = sessionCache.get(primaryUserId);
  if (cached) return cached.masterKey;

  const mySecretKey = await getCachedWrappingSecretKey();
  if (!mySecretKey) {
    throw new TrustedPartyAccessNotReadyError(
      'Unlock your vault first so we can decrypt this user\'s data.',
    );
  }

  const fetched = await vaultService.getTrustedPartyWrappedKey(primaryUserId);
  if (!fetched) throw new TrustedPartyAccessDeniedError();

  try {
    const masterKey = openSealedMasterKey(
      fetched.wrappedMasterKey,
      mySecretKey,
    );
    sessionCache.set(primaryUserId, {
      masterKey,
      role: fetched.role,
      openedAt: Date.now(),
    });
    logger.info(
      `Opened sealed master key for primary=${primaryUserId} role=${fetched.role}`,
    );
    return masterKey;
  } catch (err) {
    if (err instanceof TrustedPartyWrappingError) {
      logger.error('Sealed master-key open failed:', err);
    }
    throw err;
  }
}

/**
 * True if the session cache already holds an unwrapped primary master key
 * for the given primary. Callers use this to decide whether a screen can
 * render immediately vs. needs to wait on fetchAndOpenPrimaryMasterKey.
 */
export function hasCachedPrimaryMasterKey(primaryUserId: string): boolean {
  return sessionCache.has(primaryUserId);
}

/**
 * Clear the in-memory cache. Called on logout / background if stricter
 * session hygiene is later desired. The unwrapping secret key lives in
 * SecureStorage and is cleared there by the existing wipe paths — not
 * this module's concern.
 */
export function clearTrustedPartyAccessCache(): void {
  sessionCache.clear();
}
